import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import safeToast from "utils/safeToast";

// MDUI Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import { API_ENDPOINTS } from "config/api";
import { decryptText } from "config/encryption";

const AddressInfo = () => {
  const [addressInfo, setAddressInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [dropdowns, setDropdowns] = useState({
    districts: [],
    permLocations: [],
    tempLocations: [],
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAddressInfo = async () => {
      try {
        const userDetails = localStorage.getItem("UserLogDetails");
        if (!userDetails) throw new Error("No user details");
        
        const decryptedUser = decryptText(userDetails);
        const parsedUser = JSON.parse(decryptedUser);
        const { StudentId, Token } = parsedUser;
        
        if (!Token) {
          safeToast.error("Session expired. Please log in again.");
          localStorage.clear();
          return navigate("/BasicAuth");
        }

        const response = await axios.get(
          API_ENDPOINTS.PROFILE.GET_ADDRESS_INFO(StudentId),
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${Token}`,
            },
          }
        );

        setAddressInfo(response.data);
      } catch (error) {
        console.error("Error fetching address info:", error);
        if (error.response?.status === 401) {
          safeToast.error("Session expired. Please log in again.");
          localStorage.clear();
          navigate("/BasicAuth");
        } else {
          safeToast.error("Failed to load address info.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAddressInfo();
  }, [navigate]);

  const fetchDropdowns = async (Token) => {
    try {
      const dropdownRes = await axios.get(API_ENDPOINTS.PROFILE.GET_DROPDOWNS, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      setDropdowns((prev) => ({
        ...prev,
        districts: dropdownRes.data.districts || [],
      }));
    } catch (error) {
      console.error("Error fetching dropdowns", error);
      throw new Error("Failed to fetch dropdowns");
    }
  };

  const fetchLocationsByDistrict = async (districtId, type) => {
    try {
      const userDetails = localStorage.getItem("UserLogDetails");
      const decryptedUser = decryptText(userDetails);
      const { Token } = JSON.parse(decryptedUser);

      const response = await axios.get(
        API_ENDPOINTS.PROFILE.GET_LOCATIONS_BY_DISTRICT(districtId),
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        }
      );
      
      if (type === "perm") {
        setDropdowns((prev) => ({ ...prev, permLocations: response.data || [] }));
      } else {
        setDropdowns((prev) => ({ ...prev, tempLocations: response.data || [] }));
      }
    } catch (error) {
      console.error("Error fetching locations", error);
    }
  };

  const handleEdit = async () => {
    try {
      const userDetails = localStorage.getItem("UserLogDetails");
      const decryptedUser = decryptText(userDetails);
      const { Token } = JSON.parse(decryptedUser);

      await fetchDropdowns(Token);

      setFormData({
        permAddress: addressInfo.permAddress || "",
        permDistrictId: addressInfo.permDistrictId ? String(addressInfo.permDistrictId) : "",
        permLocationId: addressInfo.permLocationId ? String(addressInfo.permLocationId) : "",
        tempAddress: addressInfo.tempAddress || "",
        tempDistrictId: addressInfo.tempDistrictId ? String(addressInfo.tempDistrictId) : "",
        tempLocationId: addressInfo.tempLocationId ? String(addressInfo.tempLocationId) : "",
      });

      // Fetch locations for existing districts
      if (addressInfo.permDistrictId) {
        await fetchLocationsByDistrict(addressInfo.permDistrictId, "perm");
      }
      if (addressInfo.tempDistrictId) {
        await fetchLocationsByDistrict(addressInfo.tempDistrictId, "temp");
      }

      setEditMode(true);
    } catch (error) {
      safeToast.error("Unable to load dropdowns.");
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setFormData({});
    setDropdowns((prev) => ({ ...prev, permLocations: [], tempLocations: [] }));
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Fetch locations when district changes
    if (name === "permDistrictId" && value) {
      await fetchLocationsByDistrict(value, "perm");
      setFormData((prev) => ({ ...prev, permLocationId: "" })); // Reset location
    } else if (name === "tempDistrictId" && value) {
      await fetchLocationsByDistrict(value, "temp");
      setFormData((prev) => ({ ...prev, tempLocationId: "" })); // Reset location
    }
  };

  const handleSave = async () => {
    try {
      const userDetails = localStorage.getItem("UserLogDetails");
      if (!userDetails) throw new Error("User not logged in");

      const decryptedUser = decryptText(userDetails);
      const parsedUser = JSON.parse(decryptedUser);
      const { Token, StudentId, LoginId } = parsedUser;

      // Helper function to safely parse location/district IDs
      const parseId = (value) => {
        if (!value || value === "" || value === "0") return null;
        const parsed = parseInt(value);
        return isNaN(parsed) || parsed <= 0 ? null : parsed;
      };

      // Build payload - properly handle location IDs
      const payload = {
        studentId: parseInt(StudentId),
        permAddress: formData.permAddress !== undefined ? (formData.permAddress || "") : null,
        permDistrictId: parseId(formData.permDistrictId),
        permLocationId: parseId(formData.permLocationId),
        tempAddress: formData.tempAddress !== undefined ? (formData.tempAddress || "") : null,
        tempDistrictId: parseId(formData.tempDistrictId),
        tempLocationId: parseId(formData.tempLocationId),
        createdUserId: parseInt(StudentId),
        loginId: LoginId || 0,
      };

      const response = await axios.post(
        API_ENDPOINTS.PROFILE.UPDATE_ADDRESS_INFO,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Token}`,
          },
        }
      );

      if (response.status === 200) {
        safeToast.success("Address information updated successfully!");
        
        // Refetch the updated address info to get all names and updated data
        try {
          const refreshResponse = await axios.get(
            API_ENDPOINTS.PROFILE.GET_ADDRESS_INFO(StudentId),
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Token}`,
              },
            }
          );
          setAddressInfo(refreshResponse.data);
        } catch (refreshError) {
          console.error("Error refreshing address info:", refreshError);
          // Fallback: update local state with form data
          const permDistrict = dropdowns.districts.find(d => d.id === parseId(formData.permDistrictId));
          const permLocation = dropdowns.permLocations.find(l => (l.id || l.locationId) === parseId(formData.permLocationId));
          const tempDistrict = dropdowns.districts.find(d => d.id === parseId(formData.tempDistrictId));
          const tempLocation = dropdowns.tempLocations.find(l => (l.id || l.locationId) === parseId(formData.tempLocationId));
          
          setAddressInfo({
            ...addressInfo,
            permAddress: formData.permAddress || addressInfo.permAddress,
            permDistrictId: parseId(formData.permDistrictId) || addressInfo.permDistrictId,
            permDistrictName: permDistrict ? (permDistrict.txt || permDistrict.ftxt) : addressInfo.permDistrictName,
            permLocationId: parseId(formData.permLocationId) || addressInfo.permLocationId,
            permLocationName: permLocation ? (permLocation.txt || permLocation.ftxt) : addressInfo.permLocationName,
            tempAddress: formData.tempAddress || addressInfo.tempAddress,
            tempDistrictId: parseId(formData.tempDistrictId) || addressInfo.tempDistrictId,
            tempDistrictName: tempDistrict ? (tempDistrict.txt || tempDistrict.ftxt) : addressInfo.tempDistrictName,
            tempLocationId: parseId(formData.tempLocationId) || addressInfo.tempLocationId,
            tempLocationName: tempLocation ? (tempLocation.txt || tempLocation.ftxt) : addressInfo.tempLocationName,
          });
        }
        
        setEditMode(false);
        setDropdowns((prev) => ({ ...prev, permLocations: [], tempLocations: [] }));
      }
    } catch (error) {
      console.error("Error updating address info:", error);
      if (error.response?.status === 401) {
        safeToast.error("Session expired. Please log in again.");
        localStorage.clear();
        navigate("/BasicAuth");
      } else {
        safeToast.error("Failed to update address information.");
      }
    }
  };

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" py={3}>
        <CircularProgress />
      </MDBox>
    );
  }

  if (!addressInfo) {
    return (
      <MDBox textAlign="center" py={3}>
        <MDTypography variant="body2" color="error">
          No address information found.
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox py={2} px={3}>
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        {/* Card Header */}
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <MDTypography variant="h6" color="white">
            {editMode ? "Update Address Information" : "Address Information"}
          </MDTypography>
          {!editMode && (
            <Tooltip title="Edit Address Information">
              <MDButton variant="outlined" color="light" onClick={handleEdit}>
                <Icon>edit</Icon>
              </MDButton>
            </Tooltip>
          )}
        </MDBox>

        {/* Card Body */}
        <CardContent sx={{ backgroundColor: "#fafafa" }}>
          <Grid container spacing={2}>
            {/* Permanent Address Section */}
            <Grid item xs={12}>
              <MDTypography variant="h6" mb={2} color="primary">
                Permanent Address
              </MDTypography>
            </Grid>

            <Grid item xs={12}>
              <MDBox mb={2}>
                <InputLabel htmlFor="permAddress" sx={{ fontWeight: "bold", mb: 1 }}>
                  Permanent Address
                </InputLabel>
                {editMode ? (
                  <TextField
                    id="permAddress"
                    name="permAddress"
                    value={formData.permAddress || ""}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                ) : (
                  <MDTypography variant="body2">{addressInfo.permAddress || "-"}</MDTypography>
                )}
              </MDBox>
            </Grid>

            <Grid item xs={12} sm={6}>
              <MDBox mb={2}>
                <InputLabel htmlFor="permDistrictId" sx={{ fontWeight: "bold", mb: 1 }}>
                  Permanent District
                </InputLabel>
                {editMode ? (
                  <TextField
                    id="permDistrictId"
                    name="permDistrictId"
                    select
                    SelectProps={{ native: true }}
                    value={formData.permDistrictId || ""}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    variant="outlined"
                  >
                    <option value="">Select District</option>
                    {dropdowns.districts.map((district) => (
                      <option key={district.id} value={district.id}>
                        {district.txt || district.ftxt}
                      </option>
                    ))}
                  </TextField>
                ) : (
                  <MDTypography variant="body2">
                    {addressInfo.permDistrictName || "-"}
                  </MDTypography>
                )}
              </MDBox>
            </Grid>

            <Grid item xs={12} sm={6}>
              <MDBox mb={2}>
                <InputLabel htmlFor="permLocationId" sx={{ fontWeight: "bold", mb: 1 }}>
                  Permanent Location
                </InputLabel>
                {editMode ? (
                  <TextField
                    id="permLocationId"
                    name="permLocationId"
                    select
                    SelectProps={{ native: true }}
                    value={formData.permLocationId || ""}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    variant="outlined"
                    disabled={!formData.permDistrictId}
                  >
                    <option value="">Select Location</option>
                    {dropdowns.permLocations.map((location) => (
                      <option key={location.id || location.locationId} value={location.id || location.locationId}>
                        {location.txt || location.ftxt}
                      </option>
                    ))}
                  </TextField>
                ) : (
                  <MDTypography variant="body2">
                    {addressInfo.permLocationName || "-"}
                  </MDTypography>
                )}
              </MDBox>
            </Grid>

            {/* Temporary Address Section */}
            <Grid item xs={12} mt={2}>
              <MDTypography variant="h6" mb={2} color="primary">
                Temporary Address
              </MDTypography>
            </Grid>

            <Grid item xs={12}>
              <MDBox mb={2}>
                <InputLabel htmlFor="tempAddress" sx={{ fontWeight: "bold", mb: 1 }}>
                  Temporary Address
                </InputLabel>
                {editMode ? (
                  <TextField
                    id="tempAddress"
                    name="tempAddress"
                    value={formData.tempAddress || ""}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                ) : (
                  <MDTypography variant="body2">{addressInfo.tempAddress || "-"}</MDTypography>
                )}
              </MDBox>
            </Grid>

            <Grid item xs={12} sm={6}>
              <MDBox mb={2}>
                <InputLabel htmlFor="tempDistrictId" sx={{ fontWeight: "bold", mb: 1 }}>
                  Temporary District
                </InputLabel>
                {editMode ? (
                  <TextField
                    id="tempDistrictId"
                    name="tempDistrictId"
                    select
                    SelectProps={{ native: true }}
                    value={formData.tempDistrictId || ""}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    variant="outlined"
                  >
                    <option value="">Select District</option>
                    {dropdowns.districts.map((district) => (
                      <option key={district.id} value={district.id}>
                        {district.txt || district.ftxt}
                      </option>
                    ))}
                  </TextField>
                ) : (
                  <MDTypography variant="body2">
                    {addressInfo.tempDistrictName || "-"}
                  </MDTypography>
                )}
              </MDBox>
            </Grid>

            <Grid item xs={12} sm={6}>
              <MDBox mb={2}>
                <InputLabel htmlFor="tempLocationId" sx={{ fontWeight: "bold", mb: 1 }}>
                  Temporary Location
                </InputLabel>
                {editMode ? (
                  <TextField
                    id="tempLocationId"
                    name="tempLocationId"
                    select
                    SelectProps={{ native: true }}
                    value={formData.tempLocationId || ""}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    variant="outlined"
                    disabled={!formData.tempDistrictId}
                  >
                    <option value="">Select Location</option>
                    {dropdowns.tempLocations.map((location) => (
                      <option key={location.id || location.locationId} value={location.id || location.locationId}>
                        {location.txt || location.ftxt}
                      </option>
                    ))}
                  </TextField>
                ) : (
                  <MDTypography variant="body2">
                    {addressInfo.tempLocationName || "-"}
                  </MDTypography>
                )}
              </MDBox>
            </Grid>
          </Grid>

          {editMode && (
            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton variant="contained" color="info" onClick={handleSave} sx={{ mr: 1 }}>
                Save
              </MDButton>
              <MDButton variant="outlined" color="secondary" onClick={handleCancel}>
                Cancel
              </MDButton>
            </MDBox>
          )}
        </CardContent>
      </Card>
    </MDBox>
  );
};

export default AddressInfo;
